import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useHomeStore } from '@/features/home/store/home.store';
import { queryClient } from '@/shared/lib/react-query';
import type { User, AuthTokens, AuthStore } from '../types';
import type { UserRole } from '@/shared/types';

function decodeUuidFromAccessToken(token?: string): string {
  if (!token) return '';
  try {
    const [, payload] = token.split('.');
    if (!payload) return '';
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const parsed = JSON.parse(atob(padded)) as { uuid?: string };
    return String(parsed.uuid || '');
  } catch {
    return '';
  }
}

const initialState = {
  user: null as User | null,
  tokens: null as AuthTokens | null,
  isAuthenticated: false,
  isLoading: true,
  isSessionBootstrapping: false,
  error: null as string | null,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setUser: (user: User | null) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      // Strip refreshToken — it lives in the httpOnly cookie, not in JS state.
      setTokens: (tokens: AuthTokens | null) =>
        set({ tokens: tokens ? { accessToken: tokens.accessToken } : null }),

      setLoading: (isLoading: boolean) => set({ isLoading }),

      setSessionBootstrapping: (isSessionBootstrapping: boolean) =>
        set({ isSessionBootstrapping }),

      setError: (error: string | null) => set({ error }),

      login: (user: User, tokens: AuthTokens) =>
        set({
          user,
          // Only keep accessToken in memory — refreshToken stays in httpOnly cookie.
          tokens: { accessToken: tokens.accessToken },
          isAuthenticated: true,
          isLoading: false,
          isSessionBootstrapping: true,
          error: null,
        }),

      logout: () => {
        // Clear auth state synchronously so navigate() sees isAuthenticated=false immediately
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          isLoading: false,
          isSessionBootstrapping: false,
          error: null,
        });
        // Clear entire TanStack Query cache — prevents previous user's data leaking to next session
        queryClient.clear();
        // Reset home segment so next user doesn't inherit previous session's send/deliver state
        useHomeStore.getState().setSegment('send');
        // Clear brand state best-effort (non-blocking)
        import('@/features/b2b/store/brand.store')
          .then(({ useBrandStore }) => useBrandStore.getState().clearBrand())
          .catch(() => {});
      },

      updateUser: (updates: Partial<User>) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...updates } });
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'jetpacker-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        // Only persist accessToken — refreshToken lives in the httpOnly cookie.
        tokens: state.tokens ? { accessToken: state.tokens.accessToken } : null,
        isAuthenticated: state.isAuthenticated,
        isSessionBootstrapping: state.isSessionBootstrapping,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          const tokenUuid = decodeUuidFromAccessToken(state.tokens?.accessToken);
          if (state.user && !state.user.uuid && tokenUuid) {
            state.setUser({ ...state.user, uuid: tokenUuid });
          }
          // Never keep this flag stuck after page reload.
          state.setSessionBootstrapping(false);
          state.setLoading(false);
        }
      },
    }
  )
);

// Selector hooks for optimized re-renders
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useIsLoading = () => useAuthStore((state) => state.isLoading);
export const useAuthError = () => useAuthStore((state) => state.error);
export const useUserRole = () => useAuthStore((state) => state.user?.role);
export const useTokens = () => useAuthStore((state) => state.tokens);

/**
 * Derives the active navigation role from the home segment + backend KYC status.
 * - If segment is 'deliver' AND backend confirms Jett status → 'JETPACKER'
 * - Otherwise → 'SENDER'
 *
 * IMPORTANT: Does NOT trust user.isKycVerified from localStorage.
 * Instead checks: JWT role === 'JETPACKER' (backend promoted the user).
 * The full backend check via useKycStatus is done in HomePage/DeliverContent.
 * This selector uses the JWT role as a lightweight proxy that doesn't require
 * an extra API call from every component that renders Sidebar/BottomTabs.
 */
export const useActiveRole = (): UserRole => {
  const user = useAuthStore((state) => state.user);
  const segment = useHomeStore((state) => state.segment);
  // Only trust the role from the JWT (set by backend), not the local isKycVerified flag
  if (segment === 'deliver' && user?.role === 'JETPACKER') {
    return 'JETPACKER';
  }
  return 'SENDER';
};
